import { useEffect } from "react";

interface TermsOfUseModalProps {
  open: boolean;
  onClose: () => void;
}

const SECTIONS: { heading: string; body: string[] }[] = [
  {
    heading: "1. Consent and Notification Requirement",
    body: [
      "If you use SellSynq during a live meeting or interview involving another person (including candidates, customers, partners, or colleagues), you are solely responsible for notifying all participants in advance that the session is being processed, summarized, and/or analyzed by an internal system. You must obtain any consent required under applicable law (such as GDPR or CCPA) or company policy before the meeting begins. Participants must be informed that they can decline to be transcribed. SellSynq does not obtain this consent on your behalf — that responsibility rests entirely with the user running the session.",
    ],
  },
  {
    heading: "2. Intended Use",
    body: [
      "SellSynq (sellsynq.com) is intended to support revenue, sales, and account teams by summarizing meetings and interviews and surfacing helpful follow-up questions or talking points in real time. It is a productivity and support aid, not a system of record or a substitute for professional judgment. If you intend to use SellSynq in the context of a formal candidate interview or any other business-critical or evaluative process, you must first obtain approval from your manager or the relevant business owner. Use outside these intended purposes, or without proper notification to participants, is at the user's own discretion and risk.",
    ],
  },
  {
    heading: "3. No Liability for Misuse",
    body: [
      "The developers and maintainers of SellSynq assume no responsibility or liability for consequences arising from use of this system, including but not limited to failure to notify meeting participants, privacy violations, unauthorized recording or processing of a conversation, or misuse of summaries, hints, or data generated by the system. Responsibility for compliant and appropriate use rests entirely with the user and their approving manager.",
    ],
  },
  {
    heading: "4. Data Handling, Retention, and Deletion",
    body: [
      "SellSynq processes and stores only text-based data; no audio recordings are saved by the system. Any summaries, transcripts, hints, or other data generated through SellSynq are managed by the user who initiated the session. To support data minimization and privacy compliance, SellSynq provides users with direct control over their data:",
      "• Manual Deletion: Users can manually delete the transcripts of any specific meeting at any time via the system settings.",
      "• Global Opt-Out: Users can configure their settings to ensure transcripts are not saved or retained after a meeting concludes.",
      "The system owners are not responsible for how users store, share, or use this data downstream outside of the SellSynq environment.",
    ],
  },
  {
    heading: "5. No Warranty",
    body: [
      'SellSynq is provided "as is." Summaries, follow-up suggestions, and hints are generated to assist the user and may not always be accurate or complete. SellSynq does not guarantee correctness and should not be relied upon as the sole basis for decisions made during or after a meeting or interview.',
    ],
  },
  {
    heading: "6. Privacy Rights (GDPR & CCPA)",
    body: [
      "SellSynq processes data solely for the internal purposes described above. Personal data, transcripts, and summaries are not sold to third parties, satisfying CCPA requirements. Under GDPR and CCPA, individuals have the right to access, rectify, or request the deletion of their personal data. If a meeting participant requests the deletion of their data, the user who initiated the session is responsible for executing that deletion using SellSynq's built-in tools. For technical support, privacy-related inquiries, or escalating data subject requests, please contact the system administrator at support@sellsynq.com.",
    ],
  },
];

/**
 * "Sally Terms of Use" disclaimer, opened from the app footer. Follows the same
 * hand-rolled modal structure as WhatsNewModal (.modal-back / .modal / …).
 */
export function TermsOfUseModal({ open, onClose }: TermsOfUseModalProps) {
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="modal-back" style={{ zIndex: 1100 }} onClick={onClose}>
      <div className="modal" style={{ maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h3 className="modal-title">Terms of Use – SellSynq (sellsynq.com)</h3>
            <p className="modal-sub">Please read these terms before using SellSynq (sellsynq.com).</p>
          </div>
        </div>

        <div className="modal-body" style={{ maxHeight: "68vh", overflowY: "auto" }}>
          <p style={{ fontSize: 13, color: "var(--text-2)", lineHeight: 1.55, marginTop: 0 }}>
            SellSynq (sellsynq.com) is a revenue intelligence and sales coaching platform designed to summarize meetings, extract action items, and provide real-time coaching suggestions during live sessions. By using SellSynq, you acknowledge and agree to
            the following:
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {SECTIONS.map((s) => (
              <div key={s.heading}>
                <div
                  style={{ fontSize: 13, fontWeight: 700, color: "var(--text-1)", marginBottom: 4 }}
                >
                  {s.heading}
                </div>
                {s.body.map((para, i) => (
                  <p
                    key={`${s.heading}-${i}`}
                    style={{
                      fontSize: 13,
                      color: "var(--text-2)",
                      lineHeight: 1.55,
                      margin: i === 0 ? 0 : "6px 0 0",
                      paddingLeft: para.startsWith("•") ? 10 : 0,
                    }}
                  >
                    {para}
                  </p>
                ))}
              </div>
            ))}
          </div>
          <p
            style={{
              fontSize: 13,
              color: "var(--text-2)",
              lineHeight: 1.55,
              marginBottom: 0,
              marginTop: 16,
              fontWeight: 600,
            }}
          >
            By proceeding to use SellSynq, you confirm that you have read, understood, and agree to the
            terms above.
          </p>
        </div>

        <div className="modal-foot">
          <button type="button" className="pill-btn primary" onClick={onClose}>
            I understand
          </button>
        </div>
      </div>
    </div>
  );
}
